import { createContext, useContext, useState, useCallback, useEffect } from 'react';

/**
 * PeCash Internationalization System
 * 
 * LANGUAGE DEFAULTS BY ROLE:
 * - Public website: English initially, with prominent Español option
 * - U.S. Customer/Sender: English initially
 * - Dominican Beneficiary: Spanish-first
 * - Dominican Colmado/Merchant: Spanish-first
 * - Admin: English initially
 * - Investor page: English initially
 * 
 * IMPORTANT:
 * - User selection always overrides auto-detection
 * - No logout required to change language
 * - Persisted in localStorage
 * - Financial terminology preserves purchase-authorization model (no wallet language)
 */

const translations = {
  en: {
    // ============ NAVIGATION ============
    nav: {
      home: 'Home',
      senders: 'For Families',
      colmados: 'For Colmados',
      about: 'About',
      trust: 'Trust & Security',
      login: 'Log In',
      getStarted: 'Get Started',
      faq: 'FAQ',
      contact: 'Contact',
      investors: 'Investors',
      howItWorks: 'How It Works',
      pricing: 'Pricing',
    },

    // ============ LANGUAGE SELECTOR ============
    langSelector: {
      english: 'English',
      spanish: 'Español',
      switchTo: 'Switch to',
    },

    // ============ HERO SECTION ============
    hero: {
      title: 'Authorize Purchases at Dominican',
      titleHighlight: 'Colmados',
      titleEnd: 'Instantly',
      subtitle: 'Enable your family to shop at local merchants in the Dominican Republic with secure, instant purchase authorizations.',
      ctaPay: 'Authorize a Purchase',
      ctaMerchant: 'Become a Merchant',
    },

    // ============ MAIN CTA ============
    cta: {
      title: 'Ready to Get Started?',
      subtitle: 'Join thousands of families and merchants already using PeCash for secure, instant purchase authorizations.',
      startSending: 'Create Authorization',
      registerMerchant: 'Register as Merchant',
    },

    // ============ HOW IT WORKS ============
    howItWorks: {
      title: 'How It Works',
      subtitle: 'Three simple steps to authorize purchases',
      step1Title: 'Create Authorization',
      step1Desc: 'Enter the amount and select the merchant where your beneficiary can shop.',
      step2Title: 'Beneficiary Receives Access',
      step2Desc: 'Your beneficiary receives a secure link to use their purchase authorization at the selected merchant.',
      step3Title: 'Shop at Merchant',
      step3Desc: 'Beneficiary shows QR code at the merchant to complete their purchase.',
    },

    // ============ BENEFITS ============
    benefits: {
      title: 'Why PeCash?',
      subtitle: 'Built for trust, speed, and simplicity',
      senderTitle: 'For Families',
      senderItems: [
        'Real-time payment confirmation',
        'Transparent pricing, no hidden fees',
        'Pay grocery bills directly',
        'Support family from anywhere',
      ],
      merchantTitle: 'For Merchants',
      merchantItems: [
        'No bank account required',
        'Instant QR code payments',
        'Digital transaction tracking',
        'Simple settlement options',
      ],
    },

    // ============ TRUST ============
    trust: {
      title: 'Trust & Security',
      subtitle: 'Your security is our priority',
      encryption: 'Data Encryption',
      encryptionDesc: 'All transactions and personal data are protected with industry-standard encryption.',
      secure: 'Secure Processing',
      secureDesc: 'Multi-layer security protocols ensure safe payment processing.',
      fraud: 'Fraud Prevention',
      fraudDesc: 'Advanced monitoring systems to detect and prevent suspicious activity.',
      privacy: 'Data Privacy',
      privacyDesc: 'Your information is never sold. We comply with data protection regulations.',
    },

    // ============ FOOTER ============
    footer: {
      tagline: 'Connecting families and merchants across borders.',
      quickLinks: 'Quick Links',
      legal: 'Legal',
      contact: 'Contact Us',
      terms: 'Terms of Service',
      privacy: 'Privacy Policy',
      compliance: 'Compliance',
      email: 'support@pecashglobal.com',
      rights: 'All rights reserved.',
      faq: 'FAQ',
      investors: 'Investors',
    },

    // ============ SENDERS PAGE ============
    senders: {
      title: 'Support Family Purchases in the Dominican Republic',
      subtitle: 'Create purchase authorizations at participating colmados so your family can shop for groceries and essentials.',
      howTitle: 'How It Works',
      step1: 'Create your PeCash account',
      step2: 'Add your payment method',
      step3: 'Select beneficiary and colmado',
      step4: 'Authorize the purchase instantly',
      benefitsTitle: 'Benefits for Families',
      benefit1: 'Real-Time Confirmation',
      benefit1Desc: 'Know exactly when your authorization arrives.',
      benefit2: 'Transparent Pricing',
      benefit2Desc: 'See all fees upfront before you authorize.',
      benefit3: 'Merchant-Only Purchases',
      benefit3Desc: 'Authorizations can only be used at the designated colmado.',
      benefit4: 'Flexible Amounts',
      benefit4Desc: 'Authorize purchases from $10 for small needs or up to $200.',
      ctaTitle: 'Ready to Start?',
      ctaButton: 'Get Started Now',
    },

    // ============ COLMADOS PAGE ============
    colmados: {
      tagline: 'For Colmado Owners',
      title: 'Colmados: The Heart of Everyday Life',
      subtitle: 'Colmados are neighborhood stores that families rely on daily for food, household essentials, and basic goods. PeCashGlobal enables Colmados to receive secure digital payments from customers and family members abroad, without changing how you operate locally.',
      badge1: 'No equipment needed',
      badge2: 'Free to join',
      imageAlt: 'Local Dominican Colmado owner inside his neighborhood store',
      paymentFlowTitle: 'How Colmados Get Paid',
      paymentFlowSubtitle: 'A simple, secure process that works with your existing operations.',
      step1Title: 'Customer Pays',
      step1Desc: 'A customer or family member abroad pays digitally using PeCashGlobal.',
      step2Title: 'Colmado Is Notified',
      step2Desc: 'The Colmado receives a payment confirmation instantly.',
      step3Title: 'Funds Are Available',
      step3Desc: "Funds are settled through the Colmado's approved payout method.",
      onboardingTitle: 'Ready to Accept Digital Payments?',
      onboardingSubtitle: 'Join the network of trusted Colmados accepting payments from families in the US.',
      onboardingCta: 'Become a PeCashGlobal Colmado',
      onboardingDisclaimer: 'No bank required to get started. Approval subject to verification.',
      benefitsTitle: 'Why Partner With Us',
      benefit1: 'No Bank Account Needed',
      benefit1Desc: 'Start accepting payments with just your phone.',
      benefit2: 'Instant QR Payments',
      benefit2Desc: 'Customers scan, you receive—that simple.',
      benefit3: 'Track Your Settlements',
      benefit3Desc: 'See your settlement amounts and transaction history anytime.',
      benefit4: 'Flexible Settlements',
      benefit4Desc: 'Choose how and when to access your funds.',
      ctaTitle: 'Grow Your Business',
      ctaSubtitle: 'Start accepting payments today and grow your business with families abroad.',
      ctaButton: 'Get Started Free',
    },

    // ============ ABOUT PAGE ============
    about: {
      title: 'Our Mission',
      subtitle: 'Financial inclusion for everyone',
      missionTitle: 'Why We Exist',
      missionText: 'PeCashGlobal was founded to bridge the gap between diaspora communities in the United States and local businesses in the Dominican Republic. We believe everyone deserves access to simple, secure financial services.',
      valuesTitle: 'Our Values',
      value1: 'Financial Inclusion',
      value1Desc: 'Making digital payments accessible to underserved communities.',
      value2: 'Community First',
      value2Desc: 'Supporting small neighborhood merchants and family connections.',
      value3: 'Transparency',
      value3Desc: 'Clear pricing, honest communication, no hidden surprises.',
    },

    // ============ TRUST PAGE ============
    trustPage: {
      title: 'Trust, Security & Compliance',
      subtitle: 'Built with security at every layer',
      securityTitle: 'How We Protect You',
      sec1: 'End-to-End Encryption',
      sec1Desc: 'Your data is encrypted in transit and at rest using industry-standard protocols.',
      sec2: 'Secure Payment Processing',
      sec2Desc: 'We partner with established payment processors that meet global security standards.',
      sec3: 'Fraud Prevention',
      sec3Desc: 'Machine learning algorithms monitor transactions to detect and prevent fraudulent activity.',
      sec4: 'Regular Security Audits',
      sec4Desc: 'Third-party audits ensure our systems meet the highest security benchmarks.',
      privacyTitle: 'Data Privacy',
      privacyText: 'We collect only the information necessary to provide our services. Your personal data is never sold to third parties. We comply with applicable data protection regulations.',
      complianceTitle: 'Regulatory Compliance',
      complianceText: 'PeCashGlobal works with regulated and licensed payment partners and does not hold customer funds. Wallet and payment services are provided by licensed financial institutions. PeCashGlobal operates as a technology platform and program manager.',
      indicator1: 'Encryption',
      indicator2: 'Monitoring',
      indicator3: 'Uptime',
      indicator4: 'Data Breaches',
    },

    // ============ LOGIN PAGE ============
    login: {
      title: 'Sign In to PeCashGlobal',
      subtitle: 'Enter your email to receive a magic link',
      emailLabel: 'Email Address',
      emailPlaceholder: 'you@example.com',
      roleLabel: 'I am a...',
      roleSender: 'Family Supporter',
      roleMerchant: 'Colmado',
      sendButton: 'Send Magic Link',
      verifying: 'Verifying...',
      verifyingSubtext: 'Please wait while we sign you in.',
      emailSent: 'Check Your Email',
      emailSentText: 'We sent a magic link to',
      emailSentExpiry: 'The link expires in 15 minutes and can only be used once.',
      differentEmail: 'Use a different email',
      termsText: 'By signing in, you agree to our',
      termsLink: 'Terms of Service',
      privacyLink: 'Privacy Policy',
      andText: 'and',
      backHome: '← Back to Home',
      // Password login
      passwordLabel: 'Password',
      loginWithPassword: 'Log in with Password',
      orContinueWith: 'or continue with',
      forgotPassword: 'Forgot password?',
      createPassword: 'Create Password',
      confirmPassword: 'Confirm Password',
      passwordCreated: 'Password created successfully',
      passwordsDontMatch: 'Passwords do not match',
    },

    // ============ PRICING ============
    pricing: {
      serviceFee: 'PeCashGlobal service fee',
      processingFee: 'Card processing fee',
      total: 'Total',
      youPay: 'You pay',
      colmadoReceives: 'Colmado receives',
      exchangeRate: 'Exchange rate (applied at checkout)',
      rateNote: 'Rate shown before you confirm. Subject to change.',
      amount: 'Amount',
      min: 'Minimum',
      max: 'Maximum',
      feeTiers: 'Service fee tiers',
      transparentPricing: 'Transparent pricing',
      noHiddenFees: 'No hidden fees',
    },

    // ============ DASHBOARD ============
    dashboard: {
      welcome: 'Welcome back',
      authorizationLimit: 'Authorization Limit',
      recentTransactions: 'Recent Transactions',
      viewAll: 'View All',
      noTransactions: 'No transactions yet',
      status: {
        completed: 'Completed',
        pending: 'Pending',
        failed: 'Failed',
        active: 'Active',
        cancelled: 'Cancelled',
        processing: 'Processing',
        succeeded: 'Succeeded',
        requested: 'Requested',
        draft: 'Draft',
        approved: 'Approved',
        pendingApproval: 'Pending Approval',
        declined: 'Declined',
        expired: 'Expired',
        partiallyRedeemed: 'Partially Used',
        fullyRedeemed: 'Fully Used',
      },
      sender: {
        title: 'Family Dashboard',
        totalAuthorized: 'Total Authorized',
        beneficiaries: 'Family Members',
        makePayment: 'Buy for Family',
        newAuthorization: 'New Authorization',
        familyMembers: 'Family Members',
        addFamilyMember: 'Add Family Member',
        selectColmado: 'Select Colmado',
        activity: 'Activity',
        notifications: 'Notifications',
        profile: 'Profile',
        settings: 'Settings',
        help: 'Help',
        security: 'Security',
      },
      merchant: {
        title: 'Colmado Dashboard',
        todayEarnings: 'Today\'s Earnings',
        yourQRCode: 'Your QR Code',
        qrInstructions: 'Customers scan this code to authorize purchases',
        settlements: 'Payout Status',
        settlementNote: 'Payouts are processed by PeCashGlobal',
        profile: 'Profile Settings',
        payoutAmount: 'Settlement Amount',
        availableForPayout: 'Available for Payout',
        pendingPayout: 'Pending Payout',
        totalPaidOut: 'Total Paid Out',
        lastPayout: 'Last Payout',
        payoutHistory: 'Payout History',
        shareQRNote: 'Share your QR code to start receiving authorizations',
        paymentReceived: 'Authorization received',
        newPurchase: 'New Purchase',
        pendingRequests: 'Pending Requests',
        approvedOrders: 'Approved Orders',
        history: 'History',
        lookupCustomer: 'Look Up Customer',
      },
      admin: {
        title: 'Admin Dashboard',
        totalMerchants: 'Total Merchants',
        totalTransactions: 'Total Transactions',
        totalVolume: 'Total Volume',
        pendingReviews: 'Pending Reviews',
        merchantList: 'Merchant List',
        allTransactions: 'All Transactions',
        pendingPayouts: 'Pending Payouts',
        receipts: 'Receipts',
        refunds: 'Refunds',
        disputes: 'Disputes',
        webhooks: 'Webhooks',
        sms: 'SMS',
        off: 'Off',
        analytics: 'Analytics',
      },
    },

    // ============ COMMON UI ============
    common: {
      actions: 'Actions',
      view: 'View',
      cancel: 'Cancel',
      confirm: 'Confirm',
      close: 'Close',
      save: 'Save',
      delete: 'Delete',
      edit: 'Edit',
      loading: 'Loading...',
      noData: 'No data',
      all: 'All',
      logOut: 'Log Out',
      date: 'Date',
      amount: 'Amount',
      status: 'Status',
      type: 'Type',
      merchant: 'Merchant',
      colmado: 'Colmado',
      sender: 'Sender',
      beneficiary: 'Beneficiary',
      familyMember: 'Family Member',
      description: 'Description',
      error: 'Error',
      success: 'Success',
      retry: 'Retry',
      process: 'Process',
      approve: 'Approve',
      reject: 'Reject',
      decline: 'Decline',
      submit: 'Submit',
      complete: 'Complete',
      fail: 'Fail',
      export: 'Export',
      createPayout: 'Create Payout',
      search: 'Search',
      filter: 'Filter',
      refresh: 'Refresh',
      back: 'Back',
      next: 'Next',
      previous: 'Previous',
      phone: 'Phone',
      email: 'Email',
      name: 'Name',
      firstName: 'First Name',
      lastName: 'Last Name',
      required: 'Required',
      optional: 'Optional',
      yes: 'Yes',
      no: 'No',
    },

    // ============ TABLE HEADERS ============
    tables: {
      payoutId: 'Payout ID',
      receiptId: 'Receipt ID',
      refundId: 'Refund ID',
      disputeId: 'Dispute ID',
      eventId: 'Event ID',
      transactionId: 'Transaction ID',
      settlementAmount: 'Settlement Amount',
      totalReceived: 'Total Received',
      totalPaidOut: 'Total Paid Out',
      pendingPayout: 'Pending Payout',
      txns: 'Txns',
      gross: 'Gross',
      fees: 'Fees',
      net: 'Net',
      reason: 'Reason',
      evidence: 'Evidence',
      received: 'Received',
      noMerchantsYet: 'No merchants registered yet',
      noTransactionsYet: 'No transactions yet',
      noPayoutsYet: 'No payouts yet',
      noReceiptsYet: 'No receipts yet',
      noRefundsYet: 'No refunds yet',
      noDisputesYet: 'No disputes yet',
      disputesNote: 'Disputes will appear here when received from Stripe',
      noWebhooksYet: 'No webhook events recorded',
      webhooksNote: 'Events will appear here when Stripe webhooks are received',
      noSettlementData: 'No merchants with settlement amounts',
    },

    // ============ PAYOUTS ============
    payouts: {
      payoutAmount: 'Settlement Amount',
      settlementAmount: 'Settlement Amount',
      totalReceived: 'Total Received',
      alreadyPaidOut: 'Already Paid Out',
      unpaidAmount: 'Unpaid Amount',
      payoutHistory: 'Payout History',
      merchantSettlements: 'Merchant Settlements',
      createPayoutFor: 'Create Payout for',
      selectMerchant: 'Select Merchant',
      payoutCreated: 'Payout created successfully',
      payoutUpdated: 'Payout status updated',
    },

    // ============ REFUNDS ============
    refundsSection: {
      refundTransaction: 'Refund Transaction',
      refundAmount: 'Refund Amount',
      refundReason: 'Refund Reason',
      fullRefund: 'Full refund',
      partialRefund: 'Partial refund',
      processRefund: 'Process Refund',
      cancelRefund: 'Cancel Refund',
      refundSuccess: 'Refund processed successfully',
      customerRequest: 'Customer requested',
      duplicate: 'Duplicate',
      fraudulent: 'Fraudulent',
      other: 'Other',
    },

    // ============ WEBHOOKS ============
    webhooksSection: {
      last24Hours: 'Last 24 Hours',
      last7Days: 'Last 7 Days',
      total: 'Total',
      processed: 'Processed',
      successRate: 'Success Rate',
      totalEvents: 'Total Events',
      recentEvents: 'Recent Webhook Events',
      reprocessing: 'Reprocessing...',
    },

    // ============ PURCHASE AUTHORIZATION ============
    authorization: {
      // Sender UI
      sendToColmado: 'Buy at Colmado',
      selectColmado: 'Select Colmado',
      beneficiaryName: 'Family member\'s name',
      beneficiaryNamePlaceholder: 'First name only',
      beneficiaryPhone: 'Family member\'s phone',
      beneficiaryPhonePlaceholder: '+1 809 555 1234',
      amountUSD: 'Amount (USD)',
      createAuthorization: 'Create Purchase Authorization',
      authorizationCreated: 'Purchase Authorization Created',
      referenceId: 'Reference',
      authorizedAmount: 'Authorized Amount',
      expiresAt: 'Expires',
      qrInstructions: 'Show this code at the Colmado to receive goods.',
      shareQR: 'Share QR Code',
      numericCode: 'Backup Code',
      statusPending: 'Ready to Use',
      statusPartial: 'Partially Used',
      statusComplete: 'Fully Used',
      statusExpired: 'Expired',
      remaining: 'Remaining',
      redeemed: 'Used',
      myAuthorizations: 'My Authorizations',
      availableForPurchases: 'Available for Purchases',
      // Merchant/Colmado UI
      pendingRedemptions: 'Pending Purchases',
      redeemPurchase: 'Process Purchase',
      scanQR: 'Scan QR or Enter Code',
      enterCode: 'Enter code manually',
      validateCode: 'Validate Code',
      confirmDelivery: 'Confirm Goods Delivered',
      redemptionAmount: 'Value of goods delivered (DOP)',
      redemptionHistory: 'Purchase History',
      authorizedDOP: 'Authorized',
      redeemedDOP: 'Already Used',
      remainingDOP: 'Remaining',
      partialRedemption: 'Partial purchase recorded',
      fullRedemption: 'Full purchase complete',
      // Messages
      fundsReleased: 'Merchant payable created after purchase confirmation',
      noDirectWithdrawal: 'Purchases only at authorized merchant',
      payoutEligible: 'Settlement eligible after purchase confirmation',
    },

    // ============ BENEFICIARY PORTAL ============
    beneficiary: {
      welcome: 'Welcome',
      yourPurchasingAccess: 'Your Purchasing Access',
      availableAmount: 'Available for Purchases',
      remainingAmount: 'Remaining Available',
      noActiveAuthorizations: 'No active authorizations',
      waitingForFamily: 'Waiting for family member to create a purchase authorization.',
      scanAtColmado: 'Show this QR code at the colmado to make purchases.',
      codeRefreshes: 'Code refreshes every 10 minutes for security.',
      colmadoInfo: 'Colmado Information',
      validAt: 'Valid at',
      expiresOn: 'Expires on',
      // Purchase requests
      pendingRequest: 'Purchase Request',
      pendingRequestFrom: 'Purchase request from',
      requestedAmount: 'Requested Amount',
      approveRequest: 'Approve Purchase',
      declineRequest: 'Decline',
      afterApproval: 'After approval',
      beforeAmount: 'Before',
      afterAmount: 'After',
      purchaseApproved: 'Purchase Approved',
      purchaseDeclined: 'Purchase Declined',
      // Status
      preparing: 'Preparing',
      outForDelivery: 'Out for Delivery',
      delivered: 'Delivered',
      readyForPickup: 'Ready for Pickup',
      // Activity
      activityHistory: 'Activity History',
      noPurchasesYet: 'No purchases yet',
      // Help
      helpTitle: 'Need Help?',
      contactSupport: 'Contact Support',
      howToUse: 'How to Use Your Authorization',
    },

    // ============ PURCHASE REQUESTS ============
    purchaseRequest: {
      newRequest: 'New Purchase Request',
      requestPurchase: 'Request Purchase',
      enterAmount: 'Enter Amount',
      purchaseTotal: 'Purchase Total',
      waitingForApproval: 'Waiting for Approval',
      beneficiaryWillApprove: 'The beneficiary will approve this purchase.',
      requestSent: 'Request Sent',
      requestDeclined: 'Request Declined',
      requestExpired: 'Request Expired',
      requestApproved: 'Request Approved',
      pendingApproval: 'Pending Approval',
      // Delivery status
      markAsPreparing: 'Mark as Preparing',
      markAsOutForDelivery: 'Mark as Out for Delivery',
      markAsDelivered: 'Mark as Delivered',
      deliveryStatus: 'Delivery Status',
      updateStatus: 'Update Status',
    },

    // ============ PROFILE & SECURITY ============
    profile: {
      title: 'Profile',
      personalInfo: 'Personal Information',
      updateProfile: 'Update Profile',
      changesSaved: 'Changes saved successfully',
      businessInfo: 'Business Information',
      businessName: 'Business Name',
      businessAddress: 'Business Address',
      city: 'City',
      neighborhood: 'Neighborhood',
      // Security
      security: 'Security',
      password: 'Password',
      changePassword: 'Change Password',
      currentPassword: 'Current Password',
      newPassword: 'New Password',
      confirmNewPassword: 'Confirm New Password',
      passwordChanged: 'Password changed successfully',
      twoFactor: 'Two-Factor Authentication',
      enableTwoFactor: 'Enable Two-Factor Authentication',
      // Sessions
      activeSessions: 'Active Sessions',
      currentSession: 'Current Session',
      logOutAllDevices: 'Log Out All Devices',
      lastActivity: 'Last Activity',
    },

    // ============ NOTIFICATIONS ============
    notifications: {
      title: 'Notifications',
      markAllRead: 'Mark all as read',
      noNotifications: 'No notifications',
      purchaseAuthorized: 'Purchase authorized',
      purchaseCompleted: 'Purchase completed',
      newPurchaseRequest: 'New purchase request',
      requestApproved: 'Request approved',
      requestDeclined: 'Request declined',
      payoutProcessed: 'Payout processed',
      deliveryUpdate: 'Delivery update',
    },

    // ============ ERRORS ============
    errors: {
      generic: 'Something went wrong. Please try again.',
      networkError: 'Network error. Please check your connection.',
      unauthorized: 'You are not authorized to perform this action.',
      notFound: 'The requested resource was not found.',
      invalidCode: 'Invalid code. Please check and try again.',
      insufficientFunds: 'Insufficient authorized amount.',
      expired: 'This authorization has expired.',
      alreadyUsed: 'This authorization has already been fully used.',
      sessionExpired: 'Your session has expired. Please log in again.',
      invalidLink: 'This link is invalid or has expired.',
      limitExceeded: 'Transaction limit exceeded.',
      invalidAmount: 'Please enter a valid amount.',
      minAmount: 'Minimum amount is',
      maxAmount: 'Maximum amount is',
    },

    // ============ EMPTY STATES ============
    emptyStates: {
      noAuthorizations: 'No authorizations yet',
      createFirst: 'Create your first purchase authorization to get started.',
      noActivity: 'No activity yet',
      activityWillAppear: 'Your activity will appear here once you make purchases.',
      noFamilyMembers: 'No family members added',
      addFirstMember: 'Add a family member to create purchase authorizations.',
      noPurchases: 'No purchases yet',
      purchasesWillAppear: 'Completed purchases will appear here.',
    },

    // ============ FAQ PAGE ============
    faq: {
      title: 'Frequently Asked Questions',
      subtitle: 'Find answers to the most common questions about PeCash.',
      searchPlaceholder: 'Search questions...',
      categories: {
        customers: 'For Customers',
        beneficiaries: 'For Beneficiaries',
        merchants: 'For Merchants',
        payments: 'Payments & Fees',
        security: 'Security',
      },
      stillHaveQuestions: 'Still have questions?',
      contactUs: 'Contact Us',
      supportHere: 'Our support team is here to help you.',
    },

    // ============ CONTACT PAGE ============
    contact: {
      title: 'Contact Us',
      subtitle: 'Have questions? We\'re here to help.',
      contactInfo: 'Contact Information',
      sendMessage: 'Send Us a Message',
      name: 'Name',
      namePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'your@email.com',
      subject: 'Subject',
      subjects: {
        general: 'General Question',
        support: 'Technical Support',
        transaction: 'Transaction Issue',
        merchant: 'Merchant Inquiry',
        partnership: 'Business Partnership',
        press: 'Press Inquiry',
      },
      message: 'Message',
      messagePlaceholder: 'How can we help you?',
      send: 'Send Message',
      sending: 'Sending...',
      messageSent: 'Message Sent!',
      messageSentDesc: 'Thank you for contacting us. We\'ll respond to your message as soon as possible.',
      sendAnother: 'Send Another Message',
      quickLinks: 'Quick Links',
      phone: 'Phone',
      address: 'Address',
    },

    // ============ INVESTOR PAGE ============
    investor: {
      badge: 'Pre-Pilot Stage',
      heroTitle: 'Connecting Diaspora Purchasing Power',
      heroTitleHighlight: 'with Neighborhood Merchants',
      heroSubtitle: 'PeCash enables U.S. customers to pay for household purchases for family members at participating Dominican merchants. Beneficiaries receive purchasing access for the selected merchant rather than cash.',
      watchDemo: 'Watch PeCash Demo',
      requestMeeting: 'Request Investor Meeting',
      requestDataRoom: 'Data Room Access',
      // Sections
      problemTitle: 'The Problem',
      problemSubtitle: 'Traditional remittance solutions don\'t address the unique needs of families managing household expenses across borders.',
      solutionTitle: 'The PeCash Solution',
      solutionSubtitle: 'Merchant-anchored purchase authorizations that connect U.S. diaspora purchasing power with Dominican colmados — the neighborhood stores families already trust.',
      howItWorksTitle: 'How PeCash Works',
      howItWorksSubtitle: 'A simple, secure process that connects families across borders',
      marketTitle: 'Why the Dominican Republic & Colmados',
      marketSubtitle: 'Strategic market entry with strong fundamentals',
      opportunityTitle: 'Market Opportunity',
      opportunitySubtitle: 'Targeting a large and growing market with unmet needs',
      businessModelTitle: 'Business Model',
      businessModelSubtitle: 'Clear revenue streams with room for optimization during pilot',
      pricingTitle: 'Current Pricing',
      pricingSubtitle: 'Transparent, straightforward fee structure',
      competitiveTitle: 'Competitive Positioning',
      competitiveSubtitle: 'A differentiated approach to cross-border family support',
      stageTitle: 'Current Stage',
      stageSubtitle: 'Pre-pilot with product under final stabilization',
      visionTitle: 'Long-term Vision',
      visionSubtitle: 'Building toward a cross-border family-commerce network',
      teamTitle: 'Team',
      teamSubtitle: 'Dedicated to making cross-border family support simple and secure',
      fundraisingTitle: 'Fundraising',
      fundraisingSubtitle: 'Seeking partners who share our vision for family-commerce',
      ctaTitle: 'Ready to Learn More?',
      ctaSubtitle: 'Watch our product demo, schedule an investor meeting, or request access to our data room for detailed financials and documentation.',
      // Form
      meetingRequestTitle: 'Request Investor Meeting',
      dataRoomRequestTitle: 'Request Data Room Access',
      yourName: 'Your Name',
      firm: 'Firm (Optional)',
      firmRequired: 'Firm / Organization',
      interestMessage: 'Tell us about your interest in PeCash',
      accessReason: 'Why are you requesting access?',
      submitRequest: 'Submit Request',
      submitting: 'Submitting...',
      requestReceived: 'Request Received',
      meetingResponseMsg: 'We\'ll be in touch shortly to schedule your meeting.',
      dataRoomResponseMsg: 'We\'ll review your request and grant access if approved.',
    },

    // ============ LEGAL PAGES ============
    legal: {
      termsTitle: 'Terms of Service',
      privacyTitle: 'Privacy Policy',
      complianceTitle: 'Regulatory Compliance',
      lastUpdated: 'Last Updated',
      draftNotice: 'Draft - Legal Review Pending',
      importantNotice: 'Important Notice',
      legalReviewRequired: 'LEGAL REVIEW REQUIRED',
      draftDisclaimer: 'This document is a draft and requires legal review before final publication. Sections marked with "[LEGAL REVIEW REQUIRED]" contain placeholder text that must be reviewed and approved by an attorney.',
      criticalLegalNotice: 'Critical Legal Notice',
      regulatoryDisclaimer: 'PeCashGlobal\'s regulatory classification is PENDING LEGAL REVIEW. We do not state or imply:',
      regulatoryItems: [
        'Exemption from money transmitter regulation',
        'That we are not a money transmitter',
        'Regulatory approval we do not have',
        'Licenses we have not obtained',
      ],
      yourRights: 'Your Rights',
      rightsDisclaimer: 'Rights may vary by jurisdiction',
      contactPrivacy: 'For questions about this privacy policy or to exercise your rights, contact us at:',
      contactCompliance: 'For questions about our compliance practices, contact us at:',
    },

    // ============ SEO ============
    seo: {
      homeTitle: 'Authorize Family Purchases at Dominican Colmados',
      homeDesc: 'PeCash enables U.S. customers to authorize household purchases for family members at participating Dominican merchants. Secure, instant, and controlled.',
      investorTitle: 'Investors - Cross-Border Family Commerce Opportunity',
      investorDesc: 'Invest in PeCash: Building a cross-border family-commerce network connecting U.S. diaspora purchasing power with Dominican neighborhood merchants.',
      faqTitle: 'Frequently Asked Questions',
      faqDesc: 'Find answers to common questions about PeCash - how it works, fees, security, and more.',
      contactTitle: 'Contact Us',
      contactDesc: 'Have questions? Contact PeCash for support, business inquiries, or information.',
      termsTitle: 'Terms of Service',
      termsDesc: 'Terms and conditions for using PeCashGlobal services.',
      privacyTitle: 'Privacy Policy',
      privacyDesc: 'How PeCashGlobal collects, uses, and protects your personal information.',
      complianceTitle: 'Regulatory Compliance',
      complianceDesc: 'PeCashGlobal\'s commitment to legal and regulatory compliance.',
    },
  },

  // ============================================================
  // SPANISH TRANSLATIONS
  // ============================================================
  es: {
    // ============ NAVIGATION ============
    nav: {
      home: 'Inicio',
      senders: 'Para Familias',
      colmados: 'Para Colmados',
      about: 'Nosotros',
      trust: 'Confianza y Seguridad',
      login: 'Iniciar Sesión',
      getStarted: 'Comenzar',
      faq: 'Preguntas Frecuentes',
      contact: 'Contacto',
      investors: 'Inversionistas',
      howItWorks: 'Cómo Funciona',
      pricing: 'Precios',
    },

    // ============ LANGUAGE SELECTOR ============
    langSelector: {
      english: 'English',
      spanish: 'Español',
      switchTo: 'Cambiar a',
    },

    // ============ HERO SECTION ============
    hero: {
      title: 'Autoriza Compras en',
      titleHighlight: 'Colmados',
      titleEnd: 'al Instante',
      subtitle: 'Permite que tu familia compre en comercios locales en República Dominicana con autorizaciones de compra seguras e instantáneas.',
      ctaPay: 'Autorizar una Compra',
      ctaMerchant: 'Ser Comerciante',
    },

    // ============ MAIN CTA ============
    cta: {
      title: '¿Listo para comenzar?',
      subtitle: 'Únete a miles de familias y colmados que ya usan PeCash para autorizaciones de compra seguras e instantáneas.',
      startSending: 'Crear Autorización',
      registerMerchant: 'Registrarse como Colmado',
    },

    // ============ HOW IT WORKS ============
    howItWorks: {
      title: 'Cómo Funciona',
      subtitle: 'Tres simples pasos para autorizar compras',
      step1Title: 'Crear Autorización',
      step1Desc: 'Ingresa el monto y selecciona el colmado donde tu familiar puede comprar.',
      step2Title: 'Tu Familiar Recibe Acceso',
      step2Desc: 'Tu familiar recibe un enlace seguro para usar su autorización de compra en el colmado seleccionado.',
      step3Title: 'Compra en el Colmado',
      step3Desc: 'Tu familiar muestra el código QR en el colmado para completar su compra.',
    },

    // ============ BENEFITS ============
    benefits: {
      title: '¿Por qué PeCash?',
      subtitle: 'Construido para confianza, rapidez y simplicidad',
      senderTitle: 'Para Familias',
      senderItems: [
        'Confirmación en tiempo real',
        'Precios transparentes, sin tarifas ocultas',
        'Paga facturas del colmado directamente',
        'Apoya a tu familia desde cualquier lugar',
      ],
      merchantTitle: 'Para Colmados',
      merchantItems: [
        'No se requiere cuenta bancaria',
        'Pagos QR instantáneos',
        'Seguimiento digital de transacciones',
        'Opciones de liquidación simples',
      ],
    },

    // ============ TRUST ============
    trust: {
      title: 'Confianza y Seguridad',
      subtitle: 'Tu seguridad es nuestra prioridad',
      encryption: 'Encriptación de Datos',
      encryptionDesc: 'Todas las transacciones y datos personales están protegidos con encriptación estándar.',
      secure: 'Procesamiento Seguro',
      secureDesc: 'Protocolos de seguridad multicapa garantizan el procesamiento seguro.',
      fraud: 'Prevención de Fraude',
      fraudDesc: 'Sistemas avanzados de monitoreo para detectar y prevenir actividad sospechosa.',
      privacy: 'Privacidad de Datos',
      privacyDesc: 'Tu información nunca se vende. Cumplimos con las regulaciones de protección de datos.',
    },

    // ============ FOOTER ============
    footer: {
      tagline: 'Conectando familias y comerciantes a través de las fronteras.',
      quickLinks: 'Enlaces Rápidos',
      legal: 'Legal',
      contact: 'Contáctanos',
      terms: 'Términos de Servicio',
      privacy: 'Política de Privacidad',
      compliance: 'Cumplimiento',
      email: 'soporte@pecashglobal.com',
      rights: 'Todos los derechos reservados.',
      faq: 'Preguntas Frecuentes',
      investors: 'Inversionistas',
    },

    // ============ SENDERS PAGE ============
    senders: {
      title: 'Apoya las Compras de tu Familia en República Dominicana',
      subtitle: 'Crea autorizaciones de compra en colmados participantes para que tu familia pueda comprar alimentos y artículos esenciales.',
      howTitle: 'Cómo Funciona',
      step1: 'Crea tu cuenta PeCash',
      step2: 'Agrega tu método de pago',
      step3: 'Selecciona familiar y colmado',
      step4: 'Autoriza la compra al instante',
      benefitsTitle: 'Beneficios para Familias',
      benefit1: 'Confirmación en Tiempo Real',
      benefit1Desc: 'Sabe exactamente cuándo llega tu autorización.',
      benefit2: 'Precios Transparentes',
      benefit2Desc: 'Ve todas las tarifas antes de autorizar.',
      benefit3: 'Compras Solo en Colmado',
      benefit3Desc: 'Las autorizaciones solo pueden usarse en el colmado designado.',
      benefit4: 'Montos Flexibles',
      benefit4Desc: 'Autoriza compras desde $10 para necesidades pequeñas hasta $200.',
      ctaTitle: '¿Listo para Comenzar?',
      ctaButton: 'Comenzar Ahora',
    },

    // ============ COLMADOS PAGE ============
    colmados: {
      tagline: 'Para Dueños de Colmados',
      title: 'Colmados: El Corazón de la Vida Cotidiana',
      subtitle: 'Los colmados son negocios de barrio donde las familias compran diariamente alimentos, artículos del hogar y productos básicos. PeCash permite a los colmados recibir pagos digitales seguros de clientes y familiares en el extranjero, sin cambiar cómo opera tu negocio.',
      badge1: 'Sin equipo especial',
      badge2: 'Gratis para unirse',
      imageAlt: 'Dueño de colmado dominicano dentro de su negocio de barrio',
      paymentFlowTitle: 'Cómo Reciben Pagos los Colmados',
      paymentFlowSubtitle: 'Un proceso simple y seguro que funciona con tu operación actual.',
      step1Title: 'El Cliente Paga',
      step1Desc: 'Un cliente o familiar en el extranjero paga digitalmente usando PeCash.',
      step2Title: 'El Colmado Es Notificado',
      step2Desc: 'El colmado recibe una notificación de pago al instante.',
      step3Title: 'Fondos Disponibles',
      step3Desc: 'Los fondos se liquidan a través del método de pago aprobado del colmado.',
      onboardingTitle: '¿Listo para Aceptar Pagos Digitales?',
      onboardingSubtitle: 'Únete a la red de colmados confiables que reciben pagos de familias en Estados Unidos.',
      onboardingCta: 'Conviértete en un Colmado PeCash',
      onboardingDisclaimer: 'No se requiere cuenta bancaria para comenzar. Sujeto a verificación.',
      benefitsTitle: 'Por Qué Asociarte Con Nosotros',
      benefit1: 'Sin Cuenta Bancaria',
      benefit1Desc: 'Comienza a aceptar pagos solo con tu teléfono.',
      benefit2: 'Pagos QR Instantáneos',
      benefit2Desc: 'Los clientes escanean, tú recibes—así de simple.',
      benefit3: 'Rastrea tus Liquidaciones',
      benefit3Desc: 'Ve tus liquidaciones e historial de transacciones cuando quieras.',
      benefit4: 'Liquidaciones Flexibles',
      benefit4Desc: 'Elige cómo y cuándo acceder a tus fondos.',
      ctaTitle: 'Haz Crecer tu Negocio',
      ctaSubtitle: 'Comienza a aceptar pagos hoy y haz crecer tu negocio con familias en el extranjero.',
      ctaButton: 'Comenzar Gratis',
    },

    // ============ ABOUT PAGE ============
    about: {
      title: 'Nuestra Misión',
      subtitle: 'Inclusión financiera para todos',
      missionTitle: 'Por Qué Existimos',
      missionText: 'PeCash fue fundado para conectar las comunidades de la diáspora en Estados Unidos con los negocios locales en República Dominicana. Creemos que todos merecen acceso a servicios financieros simples y seguros.',
      valuesTitle: 'Nuestros Valores',
      value1: 'Inclusión Financiera',
      value1Desc: 'Haciendo los pagos digitales accesibles a comunidades desatendidas.',
      value2: 'Comunidad Primero',
      value2Desc: 'Apoyando a pequeños comerciantes y conexiones familiares.',
      value3: 'Transparencia',
      value3Desc: 'Precios claros, comunicación honesta, sin sorpresas.',
    },

    // ============ TRUST PAGE ============
    trustPage: {
      title: 'Confianza, Seguridad y Cumplimiento',
      subtitle: 'Construido con seguridad en cada capa',
      securityTitle: 'Cómo Te Protegemos',
      sec1: 'Encriptación de Extremo a Extremo',
      sec1Desc: 'Tus datos están encriptados en tránsito y en reposo usando protocolos estándar.',
      sec2: 'Procesamiento de Pagos Seguro',
      sec2Desc: 'Nos asociamos con procesadores de pago establecidos que cumplen estándares globales.',
      sec3: 'Prevención de Fraude',
      sec3Desc: 'Algoritmos monitorean transacciones para detectar actividad fraudulenta.',
      sec4: 'Auditorías de Seguridad',
      sec4Desc: 'Auditorías de terceros aseguran que nuestros sistemas cumplan los más altos estándares.',
      privacyTitle: 'Privacidad de Datos',
      privacyText: 'Solo recopilamos la información necesaria para proporcionar nuestros servicios. Tus datos personales nunca se venden. Cumplimos con las regulaciones de protección de datos aplicables.',
      complianceTitle: 'Cumplimiento Regulatorio',
      complianceText: 'PeCash trabaja con socios de pago regulados y licenciados y no retiene fondos de clientes. Los servicios de pago son proporcionados por instituciones financieras licenciadas. PeCash opera como plataforma tecnológica.',
      indicator1: 'Encriptación',
      indicator2: 'Monitoreo',
      indicator3: 'Disponibilidad',
      indicator4: 'Violaciones de Datos',
    },

    // ============ LOGIN PAGE ============
    login: {
      title: 'Inicia Sesión en PeCash',
      subtitle: 'Ingresa tu correo para recibir un enlace mágico',
      emailLabel: 'Correo Electrónico',
      emailPlaceholder: 'tu@ejemplo.com',
      roleLabel: 'Soy un...',
      roleSender: 'Familiar en EE.UU.',
      roleMerchant: 'Colmado',
      sendButton: 'Enviar Enlace Mágico',
      verifying: 'Verificando...',
      verifyingSubtext: 'Por favor espera mientras te conectamos.',
      emailSent: 'Revisa Tu Correo',
      emailSentText: 'Enviamos un enlace mágico a',
      emailSentExpiry: 'El enlace expira en 15 minutos y solo puede usarse una vez.',
      differentEmail: 'Usar otro correo',
      termsText: 'Al iniciar sesión, aceptas nuestros',
      termsLink: 'Términos de Servicio',
      privacyLink: 'Política de Privacidad',
      andText: 'y',
      backHome: '← Volver al Inicio',
      // Password login
      passwordLabel: 'Contraseña',
      loginWithPassword: 'Iniciar sesión con contraseña',
      orContinueWith: 'o continuar con',
      forgotPassword: '¿Olvidaste tu contraseña?',
      createPassword: 'Crear Contraseña',
      confirmPassword: 'Confirmar Contraseña',
      passwordCreated: 'Contraseña creada exitosamente',
      passwordsDontMatch: 'Las contraseñas no coinciden',
    },

    // ============ PRICING ============
    pricing: {
      serviceFee: 'Tarifa de servicio PeCash',
      processingFee: 'Tarifa de procesamiento de tarjeta',
      total: 'Total',
      youPay: 'Tú pagas',
      colmadoReceives: 'El colmado recibe',
      exchangeRate: 'Tipo de cambio (aplicado al confirmar)',
      rateNote: 'La tasa se muestra antes de confirmar. Puede variar.',
      amount: 'Monto',
      min: 'Mínimo',
      max: 'Máximo',
      feeTiers: 'Tarifas de servicio',
      transparentPricing: 'Precios transparentes',
      noHiddenFees: 'Sin tarifas ocultas',
    },

    // ============ DASHBOARD ============
    dashboard: {
      welcome: 'Bienvenido de nuevo',
      authorizationLimit: 'Límite de Autorización',
      recentTransactions: 'Transacciones Recientes',
      viewAll: 'Ver Todo',
      noTransactions: 'Sin transacciones aún',
      status: {
        completed: 'Completado',
        pending: 'Pendiente',
        failed: 'Fallido',
        active: 'Activo',
        cancelled: 'Cancelado',
        processing: 'Procesando',
        succeeded: 'Exitoso',
        requested: 'Solicitado',
        draft: 'Borrador',
        approved: 'Aprobado',
        pendingApproval: 'Pendiente de Aprobación',
        declined: 'Rechazado',
        expired: 'Expirado',
        partiallyRedeemed: 'Parcialmente Usado',
        fullyRedeemed: 'Completamente Usado',
      },
      sender: {
        title: 'Panel Familiar',
        totalAuthorized: 'Total Autorizado',
        beneficiaries: 'Familiares',
        makePayment: 'Comprar para mi Familia',
        newAuthorization: 'Nueva Autorización',
        familyMembers: 'Familiares',
        addFamilyMember: 'Agregar Familiar',
        selectColmado: 'Seleccionar Colmado',
        activity: 'Actividad',
        notifications: 'Notificaciones',
        profile: 'Perfil',
        settings: 'Configuración',
        help: 'Ayuda',
        security: 'Seguridad',
      },
      merchant: {
        title: 'Panel de Colmado',
        todayEarnings: 'Ganancias de Hoy',
        yourQRCode: 'Tu Código QR',
        qrInstructions: 'Los clientes escanean este código para autorizar compras',
        settlements: 'Estado de Pagos',
        settlementNote: 'Los pagos son procesados por PeCash',
        profile: 'Configuración de Perfil',
        payoutAmount: 'Monto de Liquidación',
        availableForPayout: 'Disponible para Pago',
        pendingPayout: 'Pago Pendiente',
        totalPaidOut: 'Total Pagado',
        lastPayout: 'Último Pago',
        payoutHistory: 'Historial de Pagos',
        shareQRNote: 'Comparte tu código QR para comenzar a recibir autorizaciones',
        paymentReceived: 'Autorización recibida',
        newPurchase: 'Nueva Compra',
        pendingRequests: 'Solicitudes Pendientes',
        approvedOrders: 'Pedidos Aprobados',
        history: 'Historial',
        lookupCustomer: 'Buscar Cliente',
      },
      admin: {
        title: 'Panel de Administrador',
        totalMerchants: 'Total de Colmados',
        totalTransactions: 'Total de Transacciones',
        totalVolume: 'Volumen Total',
        pendingReviews: 'Revisiones Pendientes',
        merchantList: 'Lista de Colmados',
        allTransactions: 'Todas las Transacciones',
        pendingPayouts: 'Pagos Pendientes',
        receipts: 'Recibos',
        refunds: 'Reembolsos',
        disputes: 'Disputas',
        webhooks: 'Webhooks',
        sms: 'SMS',
        off: 'Desactivado',
        analytics: 'Analíticas',
      },
    },

    // ============ COMMON UI ============
    common: {
      actions: 'Acciones',
      view: 'Ver',
      cancel: 'Cancelar',
      confirm: 'Confirmar',
      close: 'Cerrar',
      save: 'Guardar',
      delete: 'Eliminar',
      edit: 'Editar',
      loading: 'Cargando...',
      noData: 'Sin datos',
      all: 'Todo',
      logOut: 'Cerrar Sesión',
      date: 'Fecha',
      amount: 'Monto',
      status: 'Estado',
      type: 'Tipo',
      merchant: 'Colmado',
      colmado: 'Colmado',
      sender: 'Remitente',
      beneficiary: 'Beneficiario',
      familyMember: 'Familiar',
      description: 'Descripción',
      error: 'Error',
      success: 'Éxito',
      retry: 'Reintentar',
      process: 'Procesar',
      approve: 'Aprobar',
      reject: 'Rechazar',
      decline: 'Rechazar',
      submit: 'Enviar',
      complete: 'Completar',
      fail: 'Fallar',
      export: 'Exportar',
      createPayout: 'Crear Pago',
      search: 'Buscar',
      filter: 'Filtrar',
      refresh: 'Actualizar',
      back: 'Volver',
      next: 'Siguiente',
      previous: 'Anterior',
      phone: 'Teléfono',
      email: 'Correo',
      name: 'Nombre',
      firstName: 'Nombre',
      lastName: 'Apellido',
      required: 'Requerido',
      optional: 'Opcional',
      yes: 'Sí',
      no: 'No',
    },

    // ============ TABLE HEADERS ============
    tables: {
      payoutId: 'ID de Pago',
      receiptId: 'ID de Recibo',
      refundId: 'ID de Reembolso',
      disputeId: 'ID de Disputa',
      eventId: 'ID de Evento',
      transactionId: 'ID de Transacción',
      settlementAmount: 'Monto de Liquidación',
      totalReceived: 'Total Recibido',
      totalPaidOut: 'Total Pagado',
      pendingPayout: 'Pago Pendiente',
      txns: 'Trans.',
      gross: 'Bruto',
      fees: 'Tarifas',
      net: 'Neto',
      reason: 'Razón',
      evidence: 'Evidencia',
      received: 'Recibido',
      noMerchantsYet: 'No hay colmados registrados aún',
      noTransactionsYet: 'Sin transacciones aún',
      noPayoutsYet: 'Sin pagos aún',
      noReceiptsYet: 'Sin recibos aún',
      noRefundsYet: 'Sin reembolsos aún',
      noDisputesYet: 'Sin disputas aún',
      disputesNote: 'Las disputas aparecerán aquí cuando se reciban de Stripe',
      noWebhooksYet: 'Sin eventos de webhook registrados',
      webhooksNote: 'Los eventos aparecerán aquí cuando se reciban webhooks de Stripe',
      noSettlementData: 'No hay colmados con montos de liquidación',
    },

    // ============ PAYOUTS ============
    payouts: {
      payoutAmount: 'Monto de Liquidación',
      settlementAmount: 'Monto de Liquidación',
      totalReceived: 'Total Recibido',
      alreadyPaidOut: 'Ya Pagado',
      unpaidAmount: 'Monto No Pagado',
      payoutHistory: 'Historial de Pagos',
      merchantSettlements: 'Liquidaciones de Colmados',
      createPayoutFor: 'Crear Pago para',
      selectMerchant: 'Seleccionar Colmado',
      payoutCreated: 'Pago creado exitosamente',
      payoutUpdated: 'Estado de pago actualizado',
    },

    // ============ REFUNDS ============
    refundsSection: {
      refundTransaction: 'Reembolsar Transacción',
      refundAmount: 'Monto del Reembolso',
      refundReason: 'Razón del Reembolso',
      fullRefund: 'Reembolso completo',
      partialRefund: 'Reembolso parcial',
      processRefund: 'Procesar Reembolso',
      cancelRefund: 'Cancelar Reembolso',
      refundSuccess: 'Reembolso procesado exitosamente',
      customerRequest: 'Solicitud del cliente',
      duplicate: 'Duplicado',
      fraudulent: 'Fraudulento',
      other: 'Otro',
    },

    // ============ WEBHOOKS ============
    webhooksSection: {
      last24Hours: 'Últimas 24 Horas',
      last7Days: 'Últimos 7 Días',
      total: 'Total',
      processed: 'Procesados',
      successRate: 'Tasa de Éxito',
      totalEvents: 'Total de Eventos',
      recentEvents: 'Eventos de Webhook Recientes',
      reprocessing: 'Reprocesando...',
    },

    // ============ PURCHASE AUTHORIZATION ============
    authorization: {
      // Sender UI
      sendToColmado: 'Pedir al Colmado',
      selectColmado: 'Seleccionar Colmado',
      beneficiaryName: 'Nombre del familiar',
      beneficiaryNamePlaceholder: 'Solo primer nombre',
      beneficiaryPhone: 'Teléfono del familiar',
      beneficiaryPhonePlaceholder: '+1 809 555 1234',
      amountUSD: 'Monto (USD)',
      createAuthorization: 'Crear Autorización de Compra',
      authorizationCreated: 'Autorización de Compra Creada',
      referenceId: 'Referencia',
      authorizedAmount: 'Monto Autorizado',
      expiresAt: 'Expira',
      qrInstructions: 'Muestre este código en el Colmado para recibir productos.',
      shareQR: 'Compartir Código QR',
      numericCode: 'Código de Respaldo',
      statusPending: 'Listo para Usar',
      statusPartial: 'Parcialmente Usado',
      statusComplete: 'Completamente Usado',
      statusExpired: 'Expirado',
      remaining: 'Disponible Restante',
      redeemed: 'Usado',
      myAuthorizations: 'Mis Autorizaciones',
      availableForPurchases: 'Disponible para Compras',
      // Merchant/Colmado UI
      pendingRedemptions: 'Compras Pendientes',
      redeemPurchase: 'Procesar Compra',
      scanQR: 'Escanear QR o Ingresar Código',
      enterCode: 'Ingresar código manualmente',
      validateCode: 'Validar Código',
      confirmDelivery: 'Confirmar Entrega de Productos',
      redemptionAmount: 'Valor de productos entregados (RD$)',
      redemptionHistory: 'Historial de Compras',
      authorizedDOP: 'Autorizado',
      redeemedDOP: 'Ya Usado',
      remainingDOP: 'Restante',
      partialRedemption: 'Compra parcial registrada',
      fullRedemption: 'Compra completa',
      // Messages
      fundsReleased: 'Pago al colmado creado después de confirmar compra',
      noDirectWithdrawal: 'Compras solo en colmado autorizado',
      payoutEligible: 'Liquidación elegible después de confirmar compra',
    },

    // ============ BENEFICIARY PORTAL ============
    beneficiary: {
      welcome: 'Bienvenido',
      yourPurchasingAccess: 'Tu Acceso de Compra',
      availableAmount: 'Disponible para Compras',
      remainingAmount: 'Disponible Restante',
      noActiveAuthorizations: 'Sin autorizaciones activas',
      waitingForFamily: 'Esperando que tu familiar cree una autorización de compra.',
      scanAtColmado: 'Muestra este código QR en el colmado para hacer compras.',
      codeRefreshes: 'El código se actualiza cada 10 minutos por seguridad.',
      colmadoInfo: 'Información del Colmado',
      validAt: 'Válido en',
      expiresOn: 'Expira el',
      // Purchase requests
      pendingRequest: 'Solicitud de Compra',
      pendingRequestFrom: 'Solicitud de compra de',
      requestedAmount: 'Monto Solicitado',
      approveRequest: 'Aprobar Compra',
      declineRequest: 'Rechazar',
      afterApproval: 'Después de aprobar',
      beforeAmount: 'Antes',
      afterAmount: 'Después',
      purchaseApproved: 'Compra Aprobada',
      purchaseDeclined: 'Compra Rechazada',
      // Status
      preparing: 'Preparando',
      outForDelivery: 'En Camino',
      delivered: 'Entregado',
      readyForPickup: 'Listo para Recoger',
      // Activity
      activityHistory: 'Historial de Actividad',
      noPurchasesYet: 'Sin compras aún',
      // Help
      helpTitle: '¿Necesitas Ayuda?',
      contactSupport: 'Contactar Soporte',
      howToUse: 'Cómo Usar Tu Autorización',
    },

    // ============ PURCHASE REQUESTS ============
    purchaseRequest: {
      newRequest: 'Nueva Solicitud de Compra',
      requestPurchase: 'Solicitar Compra',
      enterAmount: 'Ingresar Monto',
      purchaseTotal: 'Total de Compra',
      waitingForApproval: 'Esperando Aprobación',
      beneficiaryWillApprove: 'El beneficiario aprobará esta compra.',
      requestSent: 'Solicitud Enviada',
      requestDeclined: 'Solicitud Rechazada',
      requestExpired: 'Solicitud Expirada',
      requestApproved: 'Solicitud Aprobada',
      pendingApproval: 'Pendiente de Aprobación',
      // Delivery status
      markAsPreparing: 'Marcar como Preparando',
      markAsOutForDelivery: 'Marcar como En Camino',
      markAsDelivered: 'Marcar como Entregado',
      deliveryStatus: 'Estado de Entrega',
      updateStatus: 'Actualizar Estado',
    },

    // ============ PROFILE & SECURITY ============
    profile: {
      title: 'Perfil',
      personalInfo: 'Información Personal',
      updateProfile: 'Actualizar Perfil',
      changesSaved: 'Cambios guardados exitosamente',
      businessInfo: 'Información del Negocio',
      businessName: 'Nombre del Negocio',
      businessAddress: 'Dirección del Negocio',
      city: 'Ciudad',
      neighborhood: 'Barrio',
      // Security
      security: 'Seguridad',
      password: 'Contraseña',
      changePassword: 'Cambiar Contraseña',
      currentPassword: 'Contraseña Actual',
      newPassword: 'Nueva Contraseña',
      confirmNewPassword: 'Confirmar Nueva Contraseña',
      passwordChanged: 'Contraseña cambiada exitosamente',
      twoFactor: 'Autenticación de Dos Factores',
      enableTwoFactor: 'Activar Autenticación de Dos Factores',
      // Sessions
      activeSessions: 'Sesiones Activas',
      currentSession: 'Sesión Actual',
      logOutAllDevices: 'Cerrar Sesión en Todos los Dispositivos',
      lastActivity: 'Última Actividad',
    },

    // ============ NOTIFICATIONS ============
    notifications: {
      title: 'Notificaciones',
      markAllRead: 'Marcar todo como leído',
      noNotifications: 'Sin notificaciones',
      purchaseAuthorized: 'Compra autorizada',
      purchaseCompleted: 'Compra completada',
      newPurchaseRequest: 'Nueva solicitud de compra',
      requestApproved: 'Solicitud aprobada',
      requestDeclined: 'Solicitud rechazada',
      payoutProcessed: 'Pago procesado',
      deliveryUpdate: 'Actualización de entrega',
    },

    // ============ ERRORS ============
    errors: {
      generic: 'Algo salió mal. Por favor intenta de nuevo.',
      networkError: 'Error de red. Por favor revisa tu conexión.',
      unauthorized: 'No estás autorizado para realizar esta acción.',
      notFound: 'El recurso solicitado no fue encontrado.',
      invalidCode: 'Código inválido. Por favor revisa e intenta de nuevo.',
      insufficientFunds: 'Monto autorizado insuficiente.',
      expired: 'Esta autorización ha expirado.',
      alreadyUsed: 'Esta autorización ya ha sido completamente usada.',
      sessionExpired: 'Tu sesión ha expirado. Por favor inicia sesión de nuevo.',
      invalidLink: 'Este enlace es inválido o ha expirado.',
      limitExceeded: 'Límite de transacción excedido.',
      invalidAmount: 'Por favor ingresa un monto válido.',
      minAmount: 'El monto mínimo es',
      maxAmount: 'El monto máximo es',
    },

    // ============ EMPTY STATES ============
    emptyStates: {
      noAuthorizations: 'Sin autorizaciones aún',
      createFirst: 'Crea tu primera autorización de compra para comenzar.',
      noActivity: 'Sin actividad aún',
      activityWillAppear: 'Tu actividad aparecerá aquí cuando hagas compras.',
      noFamilyMembers: 'Sin familiares agregados',
      addFirstMember: 'Agrega un familiar para crear autorizaciones de compra.',
      noPurchases: 'Sin compras aún',
      purchasesWillAppear: 'Las compras completadas aparecerán aquí.',
    },

    // ============ FAQ PAGE ============
    faq: {
      title: 'Preguntas Frecuentes',
      subtitle: 'Encuentra respuestas a las preguntas más comunes sobre PeCash.',
      searchPlaceholder: 'Buscar preguntas...',
      categories: {
        customers: 'Para Clientes',
        beneficiaries: 'Para Beneficiarios',
        merchants: 'Para Colmados',
        payments: 'Pagos y Tarifas',
        security: 'Seguridad',
      },
      stillHaveQuestions: '¿Aún tienes preguntas?',
      contactUs: 'Contáctanos',
      supportHere: 'Nuestro equipo de soporte está aquí para ayudarte.',
    },

    // ============ CONTACT PAGE ============
    contact: {
      title: 'Contáctanos',
      subtitle: '¿Tienes preguntas? Estamos aquí para ayudarte.',
      contactInfo: 'Información de Contacto',
      sendMessage: 'Envíanos un Mensaje',
      name: 'Nombre',
      namePlaceholder: 'Tu nombre',
      email: 'Correo',
      emailPlaceholder: 'tu@correo.com',
      subject: 'Asunto',
      subjects: {
        general: 'Pregunta General',
        support: 'Soporte Técnico',
        transaction: 'Problema de Transacción',
        merchant: 'Consulta de Colmado',
        partnership: 'Asociación de Negocios',
        press: 'Consulta de Prensa',
      },
      message: 'Mensaje',
      messagePlaceholder: '¿Cómo podemos ayudarte?',
      send: 'Enviar Mensaje',
      sending: 'Enviando...',
      messageSent: '¡Mensaje Enviado!',
      messageSentDesc: 'Gracias por contactarnos. Responderemos a tu mensaje lo antes posible.',
      sendAnother: 'Enviar Otro Mensaje',
      quickLinks: 'Enlaces Rápidos',
      phone: 'Teléfono',
      address: 'Dirección',
    },

    // ============ INVESTOR PAGE ============
    investor: {
      badge: 'Etapa Pre-Piloto',
      heroTitle: 'Conectando el Poder Adquisitivo de la Diáspora',
      heroTitleHighlight: 'con Comercios de Barrio',
      heroSubtitle: 'PeCash permite a clientes en EE.UU. pagar compras del hogar para familiares en colmados dominicanos participantes. Los beneficiarios reciben acceso de compra para el colmado seleccionado, no efectivo.',
      watchDemo: 'Ver Demo de PeCash',
      requestMeeting: 'Solicitar Reunión con Inversionista',
      requestDataRoom: 'Acceso al Data Room',
      // Sections
      problemTitle: 'El Problema',
      problemSubtitle: 'Las soluciones tradicionales de remesas no abordan las necesidades únicas de familias que gestionan gastos del hogar a través de fronteras.',
      solutionTitle: 'La Solución PeCash',
      solutionSubtitle: 'Autorizaciones de compra ancladas al comercio que conectan el poder adquisitivo de la diáspora estadounidense con los colmados dominicanos — los comercios de barrio que las familias ya conocen.',
      howItWorksTitle: 'Cómo Funciona PeCash',
      howItWorksSubtitle: 'Un proceso simple y seguro que conecta familias a través de fronteras',
      marketTitle: 'Por Qué República Dominicana y los Colmados',
      marketSubtitle: 'Entrada estratégica al mercado con fundamentos sólidos',
      opportunityTitle: 'Oportunidad de Mercado',
      opportunitySubtitle: 'Apuntando a un mercado grande y creciente con necesidades no satisfechas',
      businessModelTitle: 'Modelo de Negocio',
      businessModelSubtitle: 'Fuentes de ingresos claras con espacio para optimización durante el piloto',
      pricingTitle: 'Precios Actuales',
      pricingSubtitle: 'Estructura de tarifas transparente y directa',
      competitiveTitle: 'Posicionamiento Competitivo',
      competitiveSubtitle: 'Un enfoque diferenciado para el apoyo familiar transfronterizo',
      stageTitle: 'Etapa Actual',
      stageSubtitle: 'Pre-piloto con producto bajo estabilización final',
      visionTitle: 'Visión a Largo Plazo',
      visionSubtitle: 'Construyendo hacia una red de comercio familiar transfronterizo',
      teamTitle: 'Equipo',
      teamSubtitle: 'Dedicados a hacer el apoyo familiar transfronterizo simple y seguro',
      fundraisingTitle: 'Recaudación de Fondos',
      fundraisingSubtitle: 'Buscando socios que compartan nuestra visión de comercio familiar',
      ctaTitle: '¿Listo para Saber Más?',
      ctaSubtitle: 'Mira nuestro demo del producto, agenda una reunión con inversionistas, o solicita acceso a nuestro data room para información financiera detallada.',
      // Form
      meetingRequestTitle: 'Solicitar Reunión con Inversionista',
      dataRoomRequestTitle: 'Solicitar Acceso al Data Room',
      yourName: 'Tu Nombre',
      firm: 'Firma (Opcional)',
      firmRequired: 'Firma / Organización',
      interestMessage: 'Cuéntanos sobre tu interés en PeCash',
      accessReason: '¿Por qué solicitas acceso?',
      submitRequest: 'Enviar Solicitud',
      submitting: 'Enviando...',
      requestReceived: 'Solicitud Recibida',
      meetingResponseMsg: 'Nos pondremos en contacto pronto para agendar tu reunión.',
      dataRoomResponseMsg: 'Revisaremos tu solicitud y otorgaremos acceso si es aprobada.',
    },

    // ============ LEGAL PAGES ============
    legal: {
      termsTitle: 'Términos de Servicio',
      privacyTitle: 'Política de Privacidad',
      complianceTitle: 'Cumplimiento Regulatorio',
      lastUpdated: 'Última Actualización',
      draftNotice: 'Borrador - Revisión Legal Pendiente',
      importantNotice: 'Aviso Importante',
      legalReviewRequired: 'REQUIERE REVISIÓN LEGAL',
      draftDisclaimer: 'Este documento es un borrador y requiere revisión legal antes de su publicación final. Las secciones marcadas con "[REQUIERE REVISIÓN LEGAL]" contienen texto de marcador de posición que debe ser revisado y aprobado por un abogado.',
      criticalLegalNotice: 'Aviso Legal Crítico',
      regulatoryDisclaimer: 'La clasificación regulatoria de PeCash está PENDIENTE DE REVISIÓN LEGAL. No declaramos ni implicamos:',
      regulatoryItems: [
        'Exención de regulación de transmisión de dinero',
        'Que no somos un transmisor de dinero',
        'Aprobación regulatoria que no tenemos',
        'Licencias que no hemos obtenido',
      ],
      yourRights: 'Tus Derechos',
      rightsDisclaimer: 'Los derechos pueden variar según jurisdicción',
      contactPrivacy: 'Para preguntas sobre esta política de privacidad o para ejercer tus derechos, contáctanos en:',
      contactCompliance: 'Para preguntas sobre nuestras prácticas de cumplimiento, contáctanos en:',
    },

    // ============ SEO ============
    seo: {
      homeTitle: 'Autoriza Compras Familiares en Colmados Dominicanos',
      homeDesc: 'PeCash permite a clientes en EE.UU. autorizar compras del hogar para familiares en colmados dominicanos participantes. Seguro, instantáneo y controlado.',
      investorTitle: 'Inversionistas - Oportunidad de Comercio Familiar Transfronterizo',
      investorDesc: 'Invierte en PeCash: Construyendo una red de comercio familiar transfronterizo conectando el poder adquisitivo de la diáspora con comercios dominicanos.',
      faqTitle: 'Preguntas Frecuentes',
      faqDesc: 'Encuentra respuestas a preguntas comunes sobre PeCash - cómo funciona, tarifas, seguridad, y más.',
      contactTitle: 'Contáctanos',
      contactDesc: '¿Tienes preguntas? Contacta a PeCash para soporte, consultas de negocios, o información.',
      termsTitle: 'Términos de Servicio',
      termsDesc: 'Términos y condiciones para usar los servicios de PeCash.',
      privacyTitle: 'Política de Privacidad',
      privacyDesc: 'Cómo PeCash recopila, usa y protege tu información personal.',
      complianceTitle: 'Cumplimiento Regulatorio',
      complianceDesc: 'Compromiso de PeCash con el cumplimiento legal y regulatorio.',
    },
  },
};

const LanguageContext = createContext();

/**
 * Get default language based on role and browser settings
 * Also IMMEDIATELY updates document.documentElement.lang to prevent flash
 */
function getDefaultLanguage(userRole) {
  let lang = 'en';
  
  // Check if user has saved preference (highest priority)
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('pecash_language');
    if (saved && (saved === 'en' || saved === 'es')) {
      lang = saved;
    } else if (userRole === 'beneficiary' || userRole === 'merchant') {
      // Role-based defaults: Dominican users get Spanish-first
      lang = 'es';
    } else if (typeof navigator !== 'undefined') {
      // For public website, senders, admin, investors: check browser language
      const browserLang = navigator.language || navigator.userLanguage || 'en';
      if (browserLang.startsWith('es')) {
        lang = 'es';
      }
    }
  }
  
  // CRITICAL: Set document.documentElement.lang IMMEDIATELY during initialization
  // This prevents the flash where <html lang="en"> shows before React hydration
  if (typeof document !== 'undefined') {
    document.documentElement.lang = lang;
  }
  
  return lang;
}

/**
 * Synchronize HTML lang attribute and Open Graph locale meta tags
 */
function syncDocumentLanguage(lang) {
  if (typeof document === 'undefined') return;
  
  // Update HTML lang attribute for accessibility/SEO
  document.documentElement.lang = lang;
  
  // Update og:locale meta tag for social sharing
  const ogLocale = document.querySelector('meta[property="og:locale"]');
  if (ogLocale) {
    ogLocale.setAttribute('content', lang === 'es' ? 'es_DO' : 'en_US');
  }
  
  // Update og:locale:alternate meta tag
  const ogLocaleAlt = document.querySelector('meta[property="og:locale:alternate"]');
  if (ogLocaleAlt) {
    ogLocaleAlt.setAttribute('content', lang === 'es' ? 'en_US' : 'es_DO');
  }
}

export function LanguageProvider({ children, userRole = null }) {
  const [language, setLanguageState] = useState(() => getDefaultLanguage(userRole));

  // Update language if role changes (e.g., after login)
  useEffect(() => {
    const saved = localStorage.getItem('pecash_language');
    if (!saved && userRole) {
      const defaultLang = getDefaultLanguage(userRole);
      setLanguageState(defaultLang);
      syncDocumentLanguage(defaultLang);
    }
  }, [userRole]);

  // Sync HTML lang attribute and og:locale meta tags whenever language changes
  useEffect(() => {
    syncDocumentLanguage(language);
  }, [language]);

  const setLanguage = useCallback((lang) => {
    if (lang === 'en' || lang === 'es') {
      localStorage.setItem('pecash_language', lang);
      setLanguageState(lang);
    }
  }, []);

  const toggleLanguage = useCallback(() => {
    const newLang = language === 'en' ? 'es' : 'en';
    setLanguage(newLang);
  }, [language, setLanguage]);

  const t = useCallback((key, fallback = null) => {
    const keys = key.split('.');
    let value = translations[language];
    for (const k of keys) {
      value = value?.[k];
      if (value === undefined) break;
    }
    return value !== undefined ? value : (fallback || key);
  }, [language]);

  // Format currency
  const formatCurrency = useCallback((amount, currency = 'USD') => {
    const locale = language === 'es' ? 'es-DO' : 'en-US';
    const formatter = new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
    return formatter.format(amount);
  }, [language]);

  // Format DOP currency
  const formatDOP = useCallback((amount) => {
    const locale = language === 'es' ? 'es-DO' : 'en-US';
    // Use RD$ prefix for Dominican Peso
    const formatted = new Intl.NumberFormat(locale, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
    return `RD$ ${formatted}`;
  }, [language]);

  // Format date
  const formatDate = useCallback((date, options = {}) => {
    const locale = language === 'es' ? 'es-DO' : 'en-US';
    const defaultOptions = {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      ...options,
    };
    return new Intl.DateTimeFormat(locale, defaultOptions).format(new Date(date));
  }, [language]);

  // Format time
  const formatTime = useCallback((date, options = {}) => {
    const locale = language === 'es' ? 'es-DO' : 'en-US';
    const defaultOptions = {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
      ...options,
    };
    return new Intl.DateTimeFormat(locale, defaultOptions).format(new Date(date));
  }, [language]);

  // Format date and time
  const formatDateTime = useCallback((date) => {
    return `${formatDate(date)} ${formatTime(date)}`;
  }, [formatDate, formatTime]);

  // Format phone number (display only)
  const formatPhone = useCallback((phone) => {
    if (!phone) return '';
    // Keep original format, just ensure it's displayed properly
    return phone;
  }, []);

  return (
    <LanguageContext.Provider value={{ 
      language, 
      setLanguage, 
      toggleLanguage, 
      t,
      formatCurrency,
      formatDOP,
      formatDate,
      formatTime,
      formatDateTime,
      formatPhone,
      isSpanish: language === 'es',
      isEnglish: language === 'en',
    }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useLanguage must be used within a LanguageProvider');
  }
  return context;
}

export { translations };
